/**
 * Pure H OS · Sprint 12 · Operations mocks — types + live views.
 * Payload now lives in MongoDB `pureh_*` collections.
 */
import { liveArray, liveSingleton } from "../services/store";

export type BatchStage = "macerating" | "blending" | "filtering" | "bottling" | "cured" | "done" | "hold";
export type Batch = {
  id: string;
  code: string;
  sku: string;
  productName: string;
  stage: BatchStage;
  units: number;
  startedRelative: string;
  etaRelative: string;
  masterPerfumer: string;
  yieldPct: number;
  qcScore?: number;
  aiNote?: string;
};
export const BATCHES: Batch[] = liveArray<Batch>("batches");

export type QcStatus = "pass" | "hold" | "fail";
export type QcCheck = {
  id: string;
  batchCode: string;
  checkType: "olfactory" | "chromatography" | "stability" | "fill-volume" | "packaging";
  result: string;
  status: QcStatus;
  score?: number;
  performedBy: string;
  performedRelative: string;
  severity?: "high" | "medium" | "low";
};
export const QC_CHECKS: QcCheck[] = liveArray<QcCheck>("qc_checks");

export type PackStage = "queued" | "labelling" | "boxing" | "sealed" | "shipped";
export type PackagingJob = {
  id: string;
  batchCode: string;
  productName: string;
  units: number;
  stage: PackStage;
  giftWrap: boolean;
  owner: string;
  eta: string;
};
export const PACKAGING: PackagingJob[] = liveArray<PackagingJob>("packaging");

export type MaterialLevel = "critical" | "low" | "healthy";
export type Material = {
  id: string;
  sku: string;
  name: string;
  category: "raw" | "component" | "packaging";
  onHand: number;
  unit: string;
  reorderPoint: number;
  level: MaterialLevel;
  supplierId: string;
  costPerUnitEGP: number;
};
export const MATERIALS: Material[] = liveArray<Material>("materials");

export type SupplierRisk = "low" | "medium" | "high";
export type Supplier = {
  id: string;
  name: string;
  city: string;
  category: string;
  onTimeRate: number;
  qualityScore: number;
  contractExpires: string;
  risk: SupplierRisk;
  spend90d: number;
  openPOs: number;
};
export const SUPPLIERS: Supplier[] = liveArray<Supplier>("suppliers");

export type MovementKind = "receipt" | "transfer" | "adjustment" | "return" | "consumption";
export type Movement = {
  id: string;
  kind: MovementKind;
  sku: string;
  name: string;
  qty: number;
  from: string;
  to: string;
  reference: string;
  operator: string;
  timestamp: string;
};
export const MOVEMENTS: Movement[] = liveArray<Movement>("movements");

export type FulfillStage = "picking" | "packed" | "handed-to-carrier" | "in-transit" | "delivered" | "exception";
export type Fulfillment = {
  id: string;
  orderRef: string;
  customer: string;
  city: string;
  stage: FulfillStage;
  carrier: string;
  eta: string;
  weightKg: number;
  aiFlag?: string;
};
export const FULFILLMENTS: Fulfillment[] = liveArray<Fulfillment>("fulfillments");

export type RiskCategory = "supply" | "quality" | "regulatory" | "hr" | "logistics" | "cash";
export type OpsRisk = {
  id: string;
  title: string;
  category: RiskCategory;
  severity: "high" | "medium" | "low";
  probability: "high" | "medium" | "low";
  owner: string;
  status: "open" | "monitoring" | "mitigated";
  note: string;
  aiConfidence: number;
};
export const RISKS: OpsRisk[] = liveArray<OpsRisk>("risks");

type Kpi = { key: string; label: string; value: string; delta: number; trend: string; hint?: string };
export type OperationsOverview = {
  kpis: Kpi[];
  aiRisks?: { id: string; title: string; body: string }[];
  aiOpportunities?: { id: string; title: string; body: string }[];
};
export const OPERATIONS_OVERVIEW: OperationsOverview = liveSingleton<OperationsOverview>(
  "operations_overview",
  { kpis: [] },
);
